import type { OverallCompletion, ProjectPhase, PhaseKey } from '@/lib/schedule';
import { PHASE_META } from '@/lib/schedule';

/**
 * Overview lead element: the blended, labor-hour-weighted project % with a
 * stacked pill bar (each phase's segment = its CONTRIBUTION = pct × hoursShare)
 * and a legend showing each phase's own % and its share of hours. Single-phase
 * projects render one segment.
 */
export default function OverallCompletionPanel({
  completion,
  phases,
}: {
  completion: OverallCompletion;
  phases: ProjectPhase[];
}) {
  if (!phases.length) return null;

  const byKey = new Map(completion.phases.map((p) => [p.phase, p]));

  return (
    <div className="pw-card" style={{ padding: 'var(--ew-s-5)', display: 'flex', gap: 'var(--ew-s-6)', alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ minWidth: 190 }}>
        <div className="pw-kpi-label">Overall Completion</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--ew-s-2)', marginTop: 4 }}>
          <span style={{ font: 'var(--ew-fw-semibold) 38px/1 var(--ew-font)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
            {completion.overallPct}%
          </span>
          <span style={{ font: 'var(--ew-fw-regular) var(--ew-fs-meta) var(--ew-font)', color: 'var(--ew-n-400)' }}>
            weighted by labor hours
          </span>
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 240 }}>
        {/* Stacked contribution bar */}
        <div style={{ display: 'flex', height: 12, borderRadius: 'var(--ew-r-pill)', overflow: 'hidden', background: 'var(--ew-n-100)' }}>
          {phases.map((p) => {
            const c = byKey.get(p.phase);
            if (!c) return null;
            const widthPct = Math.max(0, Math.min(100, c.pct * c.hoursShare)); // contribution
            if (widthPct <= 0) return null;
            return (
              <div
                key={p.phase}
                style={{ width: `${widthPct}%`, background: PHASE_META[p.phase as PhaseKey]?.tick ?? 'var(--ew-green)' }}
                title={`${PHASE_META[p.phase as PhaseKey]?.label ?? p.phase}: ${c.pct}%`}
              />
            );
          })}
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 'var(--ew-s-5)', marginTop: 'var(--ew-s-3)', flexWrap: 'wrap' }}>
          {phases.map((p) => {
            const c = byKey.get(p.phase);
            const meta = PHASE_META[p.phase as PhaseKey];
            return (
              <span key={p.phase} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, font: 'var(--ew-fw-regular) var(--ew-fs-meta) var(--ew-font)', color: 'var(--ew-n-500)' }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: meta?.tick ?? 'var(--ew-green)' }} />
                {meta?.label ?? p.phase}
                <b style={{ color: 'var(--ew-dark)', fontWeight: 'var(--ew-fw-semibold)' as unknown as number }}>{c?.pct ?? 0}%</b>
                <span style={{ color: 'var(--ew-n-400)' }}>· {Math.round((c?.hoursShare ?? 0) * 100)}% of hours</span>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
